import type { CodCheckoutDisplay } from '@xshop/shared';
import type { PageType } from '../registry';

/**
 * Which pages a shop actually has. A theme defines what a page *looks* like; the merchant's own
 * settings decide *which* pages their storefront serves, and the two are different questions.
 *
 * The setting is `codForm.checkoutDisplay`, written only by `PUT /cod-form`. It decides how the product
 * page buys, never where `/checkout` goes — that address is always the checkout page, as on Shopify:
 *
 * - `EMBEDDED` and `POPUP` put the cash-on-delivery form on the product page itself. That page is
 *   the shop's COD lander.
 * - `ONE_PAGE` gives the form a page of its own. Then the product page is the plain one: the COD
 *   lander is what the embedded placement makes.
 *
 * Narrowing runs before the theme is asked what it ships, so the answer is always a page the shop
 * has *and* the theme draws. It applies to an installed storefront only: a preview is judging the
 * theme, so it keeps the theme's whole set.
 */

// Reports whether this shop's cash-on-delivery form lives on a page of its own.
export const sellsOnItsOwnPage = (display: CodCheckoutDisplay): boolean => display === 'ONE_PAGE';

// Narrows a requested page to the pages this shop's own settings give it, leaving the rest alone.
export function shopPage(requested: PageType, display: CodCheckoutDisplay | null): PageType {
  // Until the merchant's form has been read there is no setting to narrow by, and guessing one flickers.
  if (display === null) return requested;
  // A shop whose form has a page of its own sells from the plain product page, not the COD lander.
  return requested === 'product-cod' && sellsOnItsOwnPage(display) ? 'product' : requested;
}

// Names the pages this shop serves, out of the ones its theme ships, for anything listing them.
export function shopPageTypes(themePages: readonly PageType[], display: CodCheckoutDisplay | null): readonly PageType[] {
  if (display === null) return themePages;
  return themePages.filter((page) => shopPage(page, display) === page);
}
